import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {font, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {FormattedText} from "@khan/guide-ui";

import {isNotPlaceholder, isPlaceholder} from "../../utils.ts";
import PlaceholderBlock from "../placeholder-block.tsx";

import type {AnswerData, QuizData} from "./types.ts";

const StyledOl = addStyle("ol");
const StyledLi = addStyle("li");

interface Props {
    questions: QuizData["questions"];
}

export default function AnswerKey({questions}: Props) {
    if (isPlaceholder(questions)) {
        return null;
    }

    return (
        <>
            <LabelLarge tag="h3">
                <Trans>Answer Key</Trans>
            </LabelLarge>

            <StyledOl style={styles.questionList}>
                {questions.map((question, questionIndex) => {
                    if (isPlaceholder(question)) {
                        return (
                            <StyledLi key={questionIndex}>
                                <PlaceholderBlock
                                    placeholderKey={question.$placeholder}
                                />
                            </StyledLi>
                        );
                    }

                    const correctIndex = question.answers.findIndex(
                        (a) => isNotPlaceholder(a) && a.isCorrect,
                    );
                    if (correctIndex < 0) {
                        return <StyledLi key={questionIndex} />;
                    }

                    const answer = (
                        question.answers[correctIndex] as AnswerData
                    ).text;

                    return (
                        <StyledLi key={questionIndex}>
                            <LabelLarge style={styles.question}>
                                <FormattedText text={question.questionText} />
                            </LabelLarge>

                            <StyledOl
                                start={correctIndex + 1}
                                style={styles.answerList}
                            >
                                <StyledLi style={styles.li}>
                                    <FormattedText text={answer} />
                                </StyledLi>
                            </StyledOl>
                        </StyledLi>
                    );
                })}
            </StyledOl>
        </>
    );
}

const styles = StyleSheet.create({
    question: {
        marginBlockEnd: sizing.size_080,
    },
    questionList: {
        listStyleType: "digit",
        paddingInlineStart: sizing.size_200,
        fontSize: font.size.medium,
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_200,
    },
    answerList: {
        listStyleType: "upper-alpha",
        paddingInlineStart: sizing.size_240,
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_040,
    },
    li: {
        display: "list-item",
        paddingInlineStart: sizing.size_080,
        fontSize: font.size.medium,
    },
});
